import { Alert, Box, Divider, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { client, ContactDetailsEmail, ContactDetailsPhone, StoreProfile } from '../../app/services/api/orderManagementClient';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import LoadingWheel from '../../components/ui/LoadingWheel';
import { formatDateString } from '../../utils';

const StoreDetails = () => {
  const { t } = useTranslation('common');
  const { selectedStore } = useContext(UserContext);
  const [profile, setProfile] = useState<StoreProfile>();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const selectedStoreNumber = selectedStore?.storeNumber;

  useEffect(() => {
    let isCurrent = true;

    const loadProfile = async () => {
      if (!selectedStoreNumber) {
        setProfile(undefined);
        setError(t('storeDetails.noStore'));
        setIsLoading(false);
        return;
      }

      const requestedStoreNumber = selectedStoreNumber;
      setIsLoading(true);
      setError('');

      try {
        const result = await client.getStoreProfile(requestedStoreNumber);
        if (!isCurrent) return;

        if (result.success && result.data && result.data.storeNumber === requestedStoreNumber) {
          setProfile(result.data);
        } else if (result.success) {
          setProfile(undefined);
          setError(t('storeDetails.loadError'));
        } else {
          setProfile(undefined);
          setError(result.message || t('storeDetails.loadError'));
        }
      } catch {
        if (isCurrent) {
          setProfile(undefined);
          setError(t('storeDetails.loadError'));
        }
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    loadProfile();
    return () => {
      isCurrent = false;
    };
  }, [selectedStoreNumber, t]);

  const hasCurrentStoreProfile = Boolean(
    profile && selectedStoreNumber && profile.storeNumber === selectedStoreNumber,
  );
  const isShowingPreviousStoreProfile = Boolean(
    profile && selectedStoreNumber && profile.storeNumber !== selectedStoreNumber,
  );
  const isBusy = isLoading || isShowingPreviousStoreProfile;

  return (
    <PageArea>
      <PageTitle subTitle={t('storeDetails.subtitle')}>{t('storeDetails.title')}</PageTitle>
      {isBusy && <LoadingWheel />}
      {!isBusy && error && <Alert severity="error">{error}</Alert>}
      {!isBusy && profile && hasCurrentStoreProfile && <StoreProfileDetails profile={profile} />}
    </PageArea>
  );
};

const StoreProfileDetails = ({ profile }: { profile: StoreProfile }) => {
  const { t } = useTranslation('common');
  const { selectedStore } = useContext(UserContext);
  const emailAddresses = [profile.contactDetails?.emailAddress1, profile.contactDetails?.emailAddress2]
    .filter(hasEmailAddress);
  const phoneNumbers = [
    profile.contactDetails?.phoneNumber1,
    profile.contactDetails?.phoneNumber2,
    profile.contactDetails?.phoneNumber3,
    profile.contactDetails?.phoneNumber4,
  ].filter(hasPhoneNumber);
  const attributes = Object.entries(profile.attributes ?? {}).filter(([, value]) => value);

  return (
    <Stack spacing={4}
      divider={<Divider flexItem />}>
      <DetailsSection icon={<StorefrontOutlinedIcon />}
        title={t('storeDetails.identity')}>
        <Grid container
          spacing={3}>
          <Grid item
            xs={12}
            md={6}>
            <Detail label={t('storeDetails.storeNumber')}
              value={profile.storeNumber} />
          </Grid>
          <Grid item
            xs={12}
            md={6}>
            <Detail label={t('storeDetails.storeName')}
              value={profile.storeName} />
          </Grid>
        </Grid>
      </DetailsSection>

      <DetailsSection icon={<ContactPhoneOutlinedIcon />}
        title={t('storeDetails.contactDetails')}>
        <Grid container
          spacing={3}>
          {emailAddresses.map((email, index) => (
            <Grid item
              xs={12}
              md={6}
              key={`email-${index}`}>
              <Detail label={email.type || t('storeDetails.emailAddress')}
                value={email.emailAddress} />
            </Grid>
          ))}
          {phoneNumbers.map((phone, index) => (
            <Grid item
              xs={12}
              md={6}
              key={`phone-${index}`}>
              <Detail label={phone.type || t('storeDetails.phoneNumber')}
                value={formatPhoneNumber(phone)} />
            </Grid>
          ))}
          {!emailAddresses.length && !phoneNumbers.length && (
            <Grid item
              xs={12}><Typography color="text.secondary">{t('storeDetails.notAvailable')}</Typography></Grid>
          )}
        </Grid>
      </DetailsSection>

      <DetailsSection icon={<ScheduleOutlinedIcon />}
        title={t('storeDetails.regularOpeningHours')}>
        <TableContainer>
          <Table size="small"
            aria-label={t('storeDetails.regularOpeningHours')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('storeDetails.day')}</TableCell>
                <TableCell>{t('storeDetails.opens')}</TableCell>
                <TableCell>{t('storeDetails.closes')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(profile.openingTimes?.regularOpeningTimes ?? []).map(openingTime => (
                <TableRow key={openingTime.dayOfWeek}>
                  <TableCell>{t(`days.${getDayTranslationKey(openingTime.dayOfWeek)}`)}</TableCell>
                  <TableCell>{openingTime.openTime || t('storeDetails.notAvailable')}</TableCell>
                  <TableCell>{openingTime.closeTime || t('storeDetails.notAvailable')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DetailsSection>

      <DetailsSection icon={<ScheduleOutlinedIcon />}
        title={t('storeDetails.temporaryOpeningHours')}>
        {(profile.openingTimes?.specialDateRanges ?? []).map(dateRange => (
          <Box key={dateRange.name}>
            <Typography variant="textMD"
              fontWeight={600}
              mb={1}>{dateRange.name}</Typography>
            <TableContainer>
              <Table size="small"
                aria-label={dateRange.name}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('date')}</TableCell>
                    <TableCell>{t('storeDetails.regularOpeningHours')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dateRange.specialOpeningTimes.map(openingTime => (
                    <TableRow key={openingTime.specialDate}>
                      <TableCell>{formatProfileDate(openingTime.specialDate, selectedStore?.franchiseName)}</TableCell>
                      <TableCell>{formatOpeningTime(openingTime.openTime, openingTime.closeTime, t('storeDetails.closed'))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
        {!profile.openingTimes?.specialDateRanges?.some(range => range.specialOpeningTimes.length) && (
          <Typography color="text.secondary">{t('storeDetails.noTemporaryHours')}</Typography>
        )}
      </DetailsSection>

      <DetailsSection icon={<InfoOutlinedIcon />}
        title={t('storeDetails.operationalDetails')}>
        <Grid container
          spacing={3}>
          <OperationalDetail label={t('storeDetails.deliveryRestrictions')}
            value={profile.openingTimes?.deliveryTimesRestrictions} />
          <OperationalDetail label={t('storeDetails.accessRestrictions')}
            value={profile.openingTimes?.accessRestrictions} />
          <OperationalDetail label={t('storeDetails.securityAccess')}
            value={profile.openingTimes?.accessOnlyThroughSecurity} />
          <OperationalDetail label={t('storeDetails.generalComments')}
            value={profile.openingTimes?.generalComments} />
        </Grid>
      </DetailsSection>

      <DetailsSection icon={<InfoOutlinedIcon />}
        title={t('storeDetails.attributes')}>
        <Grid container
          spacing={3}>
          {attributes.map(([key, value]) => (
            <Grid item
              xs={12}
              md={6}
              key={key}>
              <Detail label={formatAttributeName(key)}
                value={value} />
            </Grid>
          ))}
          {!attributes.length && (
            <Grid item
              xs={12}><Typography color="text.secondary">{t('storeDetails.noAttributes')}</Typography></Grid>
          )}
        </Grid>
      </DetailsSection>
    </Stack>
  );
};

const DetailsSection = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
  <Box component="section">
    <Stack direction="row"
      alignItems="center"
      spacing={1}
      mb={2}
      color="primary.main">
      {icon}
      <Typography component="h2"
        variant="textLG"
        fontWeight={600}>{title}</Typography>
    </Stack>
    {children}
  </Box>
);

const Detail = ({ label, value }: { label: string; value?: string }) => (
  <Stack direction="row"
    spacing={1}
    alignItems="baseline">
    <Typography variant="textSM"
      color="text.secondary">{label}:</Typography>
    <Typography variant="textMD"
      sx={{ overflowWrap: 'anywhere' }}>{value || '-'}</Typography>
  </Stack>
);

const OperationalDetail = ({ label, value }: { label: string; value?: string }) => (
  <Grid item
    xs={12}
    md={6}><Detail label={label}
      value={value} /></Grid>
);

const hasEmailAddress = (email?: ContactDetailsEmail): email is ContactDetailsEmail & { emailAddress: string } =>
  Boolean(email?.emailAddress);

const hasPhoneNumber = (phone?: ContactDetailsPhone): phone is ContactDetailsPhone & { mainPhoneNo: string } =>
  Boolean(phone?.mainPhoneNo);

const formatPhoneNumber = (phone: ContactDetailsPhone) =>
  [phone.countryCode, phone.mainPhoneNo, phone.extension].filter(Boolean).join(' ');

const formatOpeningTime = (openTime: string, closeTime: string, closedLabel: string) =>
  openTime && closeTime ? `${openTime} - ${closeTime}` : closedLabel;

export const getDayTranslationKey = (dayOfWeek: string) => ({
  Monday: 'Mon',
  Tuesday: 'Tues',
  Wednesday: 'Wed',
  Thursday: 'Thurs',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
}[dayOfWeek] ?? dayOfWeek);

const formatProfileDate = (date: string, franchiseName?: string | null) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  return formatDateString(match ? `${match[3]}-${match[2]}-${match[1]}` : date, franchiseName);
};

const formatAttributeName = (key: string) => key
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .replace(/^./, character => character.toUpperCase());

export default StoreDetails;
